/**
 * CMMD user settings document sync — the all-postures consumer of CMMD's
 * user-scoped settings resource (forge#3515).
 *
 * Before this module, only cloud shells converged: CloudStateSync mirrored
 * settings.json / keybindings.json into a per-shell Postgres KV, so desktop
 * and sprites kept whatever their local files held. The CMMD resource is the
 * one document store every posture can reach through the owner lane
 * (/api/cmmd/* with the CMMD user JWT), so this module hydrates from it and
 * publishes local changes back to it.
 *
 * Fallback stays local-file: when CMMD is not connected (desktop-local, an
 * unauthenticated session) or a request fails, the local server files remain
 * authoritative and nothing is written.
 *
 * Conflict rule is deterministic: the stored revision wins. A writer whose
 * revision went stale gets 409, re-reads the stored document, adopts its
 * revision, and applies it locally. Two runtimes racing converge on the
 * stored document instead of interleaving halves of each other's state.
 *
 * The core in this file is pure and injectable; the concrete wiring that
 * binds it to cmmdApiClient, the server-state store and the primary
 * environment connection lives in userSettingsSyncClient.ts.
 */
import type {
  CmmdUserSettingsDocument,
  CmmdUserSettingsNamespace,
  KeybindingRule,
  ResolvedKeybindingsConfig,
  PutCmmdUserSettingsParams,
} from "@cmmd-forge/contracts";
import {
  DEFAULT_KEYBINDINGS,
  encodeShortcut,
  encodeWhenAst,
  parseKeybindingWhenExpression,
} from "@cmmd-forge/shared/keybindings";

export const USER_SETTINGS_SYNC_NAMESPACES = [
  "ui-settings",
  "keybindings",
] as const satisfies readonly CmmdUserSettingsNamespace[];

export type UserSettingsSyncStatus =
  /** Remote document existed and differed; it was applied locally. */
  | "applied"
  /** Remote and local already agree. */
  | "in-sync"
  /** CMMD had no document; the local document was seeded as revision 1. */
  | "seeded"
  /** The local document was stored under the known revision. */
  | "published"
  /** Our write was stale; the stored document won and was applied locally. */
  | "remote-won"
  /** No usable CMMD credential — local files stay authoritative. */
  | "skipped-unauthenticated"
  /** CMMD is not connected for this session (intake says so). */
  | "skipped-disconnected"
  /** Network/5xx/unknown failure — retryable on the next trigger. */
  | "failed";

/** Reads and writes one user's settings documents on CMMD. */
export interface CmmdUserSettingsGateway {
  get(namespace: CmmdUserSettingsNamespace): Promise<CmmdUserSettingsDocument>;
  put(
    namespace: CmmdUserSettingsNamespace,
    params: PutCmmdUserSettingsParams,
  ): Promise<CmmdUserSettingsDocument>;
}

export type GatewayFailureKind = "auth" | "not-found" | "conflict" | "other";

export interface CmmdUserSettingsGatewayError {
  readonly kind: GatewayFailureKind;
}

/**
 * Reads the local document a namespace maps to, and applies a remote document
 * to the local runtime (the forge server files, through the same write paths
 * the settings UI uses).
 */
export interface CmmdUserSettingsApplier {
  readLocalDocument(namespace: CmmdUserSettingsNamespace): Record<string, unknown>;
  applyRemoteDocument(
    namespace: CmmdUserSettingsNamespace,
    document: Record<string, unknown>,
  ): Promise<void>;
}

/** The custom keybindings document as stored in keybindings.json on the server. */
export type KeybindingsDocument = {
  readonly bindings: readonly KeybindingRule[];
};

/**
 * The `when` expression a resolved rule's AST renders to. Defaults are
 * written `!terminalFocus` while an AST round-trip renders `!(terminalFocus)`,
 * so raw string comparison would report every default as a custom binding.
 */
function normalizeWhenExpression(when: string): string {
  const parsed = parseKeybindingWhenExpression(when);
  return parsed ? encodeWhenAst(parsed) : when;
}

/**
 * Reduce the resolved runtime keybindings to the sparse custom set: only the
 * rules whose command differs from the shipped default. Syncing the resolved
 * set would pin every default as a custom override on the target machine and
 * freeze it against future default changes.
 */
export function deriveSparseKeybindings(resolved: ResolvedKeybindingsConfig): KeybindingsDocument {
  const defaultsByCommand = new Map<string, KeybindingRule[]>();
  for (const rule of DEFAULT_KEYBINDINGS) {
    defaultsByCommand.set(rule.command, [...(defaultsByCommand.get(rule.command) ?? []), rule]);
  }
  const bindings: KeybindingRule[] = [];
  for (const rule of resolved) {
    const commandDefaults = defaultsByCommand.get(rule.command) ?? [];
    const key = encodeShortcut(rule.shortcut);
    if (key === null) continue;
    const when = rule.whenAst ? encodeWhenAst(rule.whenAst) : undefined;
    // A command can ship several default bindings; the rule is default when
    // any of them matches.
    const isDefault = commandDefaults.some((defaultRule) => {
      const defaultWhen =
        defaultRule.when === undefined ? undefined : normalizeWhenExpression(defaultRule.when);
      return defaultRule.key === key && defaultWhen === when;
    });
    if (isDefault) continue;
    const asFileRule: KeybindingRule = when
      ? { command: rule.command, key, when }
      : { command: rule.command, key };
    bindings.push(asFileRule);
  }
  return { bindings };
}

export interface CmmdUserSettingsSyncLogger {
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
}

export interface CreateCmmdUserSettingsSyncInput {
  readonly gateway: CmmdUserSettingsGateway;
  readonly applier: CmmdUserSettingsApplier;
  readonly log: CmmdUserSettingsSyncLogger;
}

/** Stable structural comparison so an applied document is recognized as its own echo. */
export function settingsDocumentsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(sortDocumentKeys(a)) === JSON.stringify(sortDocumentKeys(b));
}

function sortDocumentKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortDocumentKeys);
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).toSorted(([a], [b]) =>
      a.localeCompare(b),
    );
    return Object.fromEntries(entries.map(([key, item]) => [key, sortDocumentKeys(item)]));
  }
  return value;
}

export interface CmmdUserSettingsSync {
  hydrateAll(): Promise<ReadonlyMap<CmmdUserSettingsNamespace, UserSettingsSyncStatus>>;
  hydrateNamespace(namespace: CmmdUserSettingsNamespace): Promise<UserSettingsSyncStatus>;
  /** Called when the local document for a namespace changed outside this module. */
  publishNamespace(namespace: CmmdUserSettingsNamespace): Promise<UserSettingsSyncStatus>;
}

export function createCmmdUserSettingsSync(
  input: CreateCmmdUserSettingsSyncInput,
): CmmdUserSettingsSync {
  const { gateway, applier, log } = input;
  /** Last revision known to be stored on CMMD, per namespace. */
  const revisions = new Map<CmmdUserSettingsNamespace, number>();
  /**
   * The document content this module last knew to be true on both sides.
   * Comparing against this is what makes the echo of our own remote
   * application a no-op instead of a second write.
   */
  const lastSyncedDocuments = new Map<CmmdUserSettingsNamespace, unknown>();
  const pendingOperations = new Map<CmmdUserSettingsNamespace, Promise<void>>();

  function enqueue(
    namespace: CmmdUserSettingsNamespace,
    operation: () => Promise<UserSettingsSyncStatus>,
  ): Promise<UserSettingsSyncStatus> {
    const result = (pendingOperations.get(namespace) ?? Promise.resolve()).then(operation);
    // A rejected operation must not prevent later retries for this namespace.
    pendingOperations.set(
      namespace,
      result.then(
        () => undefined,
        () => undefined,
      ),
    );
    return result;
  }

  function isGatewayError(error: unknown): error is CmmdUserSettingsGatewayError {
    return (
      typeof error === "object" &&
      error !== null &&
      typeof (error as CmmdUserSettingsGatewayError).kind === "string"
    );
  }

  async function getRemote(namespace: CmmdUserSettingsNamespace): Promise<
    | { readonly ok: true; readonly document: CmmdUserSettingsDocument }
    | {
        readonly ok: false;
        readonly status: UserSettingsSyncStatus;
        readonly kind: GatewayFailureKind;
      }
  > {
    try {
      return { ok: true, document: await gateway.get(namespace) };
    } catch (error) {
      const kind = isGatewayError(error) ? error.kind : "other";
      if (kind === "auth") return { ok: false, status: "skipped-unauthenticated", kind };
      if (kind === "other") log.warn("user settings fetch failed", { namespace, kind });
      return { ok: false, status: "failed", kind };
    }
  }

  async function applyRemote(
    namespace: CmmdUserSettingsNamespace,
    document: CmmdUserSettingsDocument,
  ): Promise<void> {
    await applier.applyRemoteDocument(namespace, document.document);
    revisions.set(namespace, document.revision);
    lastSyncedDocuments.set(namespace, document.document);
  }

  async function seedFromLocal(
    namespace: CmmdUserSettingsNamespace,
  ): Promise<UserSettingsSyncStatus> {
    const local = applier.readLocalDocument(namespace);
    try {
      const stored = await gateway.put(namespace, {
        document: local,
        schemaVersion: 1,
        revision: 0,
      });
      revisions.set(namespace, stored.revision);
      lastSyncedDocuments.set(namespace, local);
      return "seeded";
    } catch (error) {
      const kind = isGatewayError(error) ? error.kind : "other";
      if (kind === "auth") return "skipped-unauthenticated";
      if (kind === "conflict") {
        // Another runtime created the document between our GET and PUT.
        // Converge the ordinary way: the stored revision wins.
        return publishNamespace(namespace);
      }
      log.warn("user settings seed failed", { namespace, kind });
      return "failed";
    }
  }

  async function hydrateNamespace(
    namespace: CmmdUserSettingsNamespace,
  ): Promise<UserSettingsSyncStatus> {
    const remote = await getRemote(namespace);
    if (!remote.ok) {
      if (remote.kind === "not-found") {
        // A 404 means CMMD has nothing stored yet; seed it with the local file
        // so the first authenticated runtime publishes the user's settings.
        return seedFromLocal(namespace);
      }
      return remote.status;
    }

    const local = applier.readLocalDocument(namespace);
    if (settingsDocumentsEqual(local, remote.document.document)) {
      revisions.set(namespace, remote.document.revision);
      lastSyncedDocuments.set(namespace, remote.document.document);
      return "in-sync";
    }
    await applyRemote(namespace, remote.document);
    return "applied";
  }

  async function publishNamespace(
    namespace: CmmdUserSettingsNamespace,
  ): Promise<UserSettingsSyncStatus> {
    const revision = revisions.get(namespace);
    // Never hydrated on this runtime: a GET first, not a blind write.
    if (revision === undefined) return hydrateNamespace(namespace);

    const local = applier.readLocalDocument(namespace);
    // The echo of our own remote application lands here, not in a write.
    if (settingsDocumentsEqual(local, lastSyncedDocuments.get(namespace))) {
      return "in-sync";
    }

    try {
      return await putLocal(namespace, local, revision);
    } catch (error) {
      return handlePublishFailure(namespace, error);
    }
  }

  async function putLocal(
    namespace: CmmdUserSettingsNamespace,
    local: Record<string, unknown>,
    revision: number,
  ): Promise<UserSettingsSyncStatus> {
    const stored = await gateway.put(namespace, {
      document: local,
      schemaVersion: 1,
      revision,
    });
    revisions.set(namespace, stored.revision);
    lastSyncedDocuments.set(namespace, local);
    return "published";
  }

  async function adoptStoredDocument(
    namespace: CmmdUserSettingsNamespace,
  ): Promise<UserSettingsSyncStatus> {
    // Deterministic resolution: the stored revision wins. Re-read it, adopt
    // its revision, and make the local runtime match.
    const remote = await getRemote(namespace);
    if (!remote.ok) return remote.status;
    const local = applier.readLocalDocument(namespace);
    if (!settingsDocumentsEqual(local, remote.document.document)) {
      await applyRemote(namespace, remote.document);
    } else {
      revisions.set(namespace, remote.document.revision);
      lastSyncedDocuments.set(namespace, remote.document.document);
    }
    return "remote-won";
  }

  async function handlePublishFailure(
    namespace: CmmdUserSettingsNamespace,
    error: unknown,
  ): Promise<UserSettingsSyncStatus> {
    const kind = isGatewayError(error) ? error.kind : "other";
    if (kind === "auth") return "skipped-unauthenticated";
    if (kind === "conflict") return adoptStoredDocument(namespace);
    log.warn("user settings publish failed", { namespace, kind });
    return "failed";
  }

  return {
    async hydrateAll() {
      const statuses = new Map<CmmdUserSettingsNamespace, UserSettingsSyncStatus>();
      for (const namespace of USER_SETTINGS_SYNC_NAMESPACES) {
        try {
          statuses.set(namespace, await enqueue(namespace, () => hydrateNamespace(namespace)));
        } catch (error) {
          log.error("user settings hydrate crashed", { namespace, error });
          statuses.set(namespace, "failed");
        }
      }
      return statuses;
    },
    hydrateNamespace: (namespace) => enqueue(namespace, () => hydrateNamespace(namespace)),
    publishNamespace: (namespace) => enqueue(namespace, () => publishNamespace(namespace)),
  };
}
